import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { Banknote, FileText, MapPin, Package, Ruler, Truck, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "@/api";
import { ErrorState } from "@/components/common/states";
import { useFormatPrice } from "@/lib/use-format-price";
import { Skeleton } from "@osmix/ui/skeleton";

// Страница «Доставка» — нормальный раздел вместо ссылки на сырой PDF. Тарифы и
// порог бесплатной доставки берём из реального гейтвея (api.settings.delivery),
// а не хардкодим — те же числа, что считает корзина. Полные правила (примерка,
// частичный выкуп, оплата на ПВЗ) — в PDF внизу, он же в подвале.
const DELIVERY_RULES_PDF = "/docs/b2c/Правила доставки и примерки OSMIX.pdf";
export function DeliveryPage() {
  const $ = _c(81);
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["settings", "delivery"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const config = useQuery(t0);
  const fees = config.data?.fees;
  const freeOver = config.data?.freeOver;
  let t1;
  let t2;
  let t3;
  if ($[1] !== config || $[2] !== fees?.express || $[3] !== fees?.pickup || $[4] !== fees?.standard || $[5] !== fmt || $[6] !== freeOver || $[7] !== t) {
    const methods = [{
      key: "standard",
      icon: Truck,
      title: t("delivery.standard.title", {
        defaultValue: "\u041A\u0443\u0440\u044C\u0435\u0440"
      }),
      desc: t("delivery.standard.desc", {
        defaultValue: "\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430 \u043A\u0443\u0440\u044C\u0435\u0440\u043E\u043C \u0421\u0414\u042D\u041A \u043F\u043E \u0430\u0434\u0440\u0435\u0441\u0443. \u0421\u0440\u043E\u043A\u0438 \u0437\u0430\u0432\u0438\u0441\u044F\u0442 \u043E\u0442 \u0433\u043E\u0440\u043E\u0434\u0430 \u0438 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0430\u044E\u0442\u0441\u044F \u043F\u0440\u0438 \u043E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u0438\u0438."
      }),
      fee: fees?.standard
    }, {
      key: "express",
      icon: Zap,
      title: t("delivery.express.title", {
        defaultValue: "\u042D\u043A\u0441\u043F\u0440\u0435\u0441\u0441-\u043A\u0443\u0440\u044C\u0435\u0440"
      }),
      desc: t("delivery.express.desc", {
        defaultValue: "\u0423\u0441\u043A\u043E\u0440\u0435\u043D\u043D\u0430\u044F \u043A\u0443\u0440\u044C\u0435\u0440\u0441\u043A\u0430\u044F \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0430, \u043A\u043E\u0433\u0434\u0430 \u043D\u0443\u0436\u043D\u043E \u0431\u044B\u0441\u0442\u0440\u0435\u0435 \u043E\u0431\u044B\u0447\u043D\u043E\u0433\u043E."
      }),
      fee: fees?.express
    }, {
      key: "pickup",
      icon: MapPin,
      title: t("delivery.pickup.title", {
        defaultValue: "\u041F\u0443\u043D\u043A\u0442 \u0432\u044B\u0434\u0430\u0447\u0438"
      }),
      desc: t("delivery.pickup.desc", {
        defaultValue: "\u041F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u0435 \u0432 \u043F\u0443\u043D\u043A\u0442\u0435 \u0432\u044B\u0434\u0430\u0447\u0438 \u0421\u0414\u042D\u041A. \u041F\u0443\u043D\u043A\u0442 \u043C\u043E\u0436\u043D\u043E \u0432\u044B\u0431\u0440\u0430\u0442\u044C \u043D\u0430 \u043A\u0430\u0440\u0442\u0435 \u043F\u0440\u0438 \u043E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u0438\u0438 \u0437\u0430\u043A\u0430\u0437\u0430."
      }),
      fee: fees?.pickup
    }] as const;
    t1 = "flex flex-col gap-8";
    let t4;
    if ($[11] !== t) {
      t4 = t("catalog.breadcrumbHome", {
        defaultValue: "\u0413\u043B\u0430\u0432\u043D\u0430\u044F"
      });
      $[11] = t;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t4) {
      t5 = <Link to="/" className="hover:text-osmix-tangelo">{t4}</Link>;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t) {
      t6 = t("delivery.title", {
        defaultValue: "\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430"
      });
      $[15] = t;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t6) {
      t7 = <span className="text-[#1F1F1F]">{t6}</span>;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t5 || $[20] !== t7) {
      t8 = <nav aria-label={"\u0425\u043B\u0435\u0431\u043D\u044B\u0435 \u043A\u0440\u043E\u0448\u043A\u0438"} className="flex flex-wrap items-center gap-3 text-[14px] leading-[18px] text-black/50">{t5}{t7}</nav>;
      $[19] = t5;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== t) {
      t9 = t("delivery.title", {
        defaultValue: "\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430"
      });
      $[22] = t;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t9) {
      t10 = <h1 className="type-h1-bold">{t9}</h1>;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== t) {
      t11 = t("delivery.intro", {
        defaultValue: "\u0422\u043E\u0432\u0430\u0440\u044B \u0440\u0443\u0447\u043D\u043E\u0439 \u0440\u0430\u0431\u043E\u0442\u044B \u0435\u0434\u0443\u0442 \u043A \u0432\u0430\u043C \u0447\u0435\u0440\u0435\u0437 \u0421\u0414\u042D\u041A \u043F\u043E \u0432\u0441\u0435\u0439 \u0420\u043E\u0441\u0441\u0438\u0438: \u043A\u0443\u0440\u044C\u0435\u0440\u043E\u043C \u0434\u043E \u0434\u0432\u0435\u0440\u0438 \u0438\u043B\u0438 \u0432 \u043F\u0443\u043D\u043A\u0442 \u0432\u044B\u0434\u0430\u0447\u0438. \u0421\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u044C \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0438 \u0441\u0447\u0438\u0442\u0430\u0435\u0442\u0441\u044F \u043F\u043E \u043A\u0430\u0436\u0434\u043E\u043C\u0443 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0443 \u0432 \u0437\u0430\u043A\u0430\u0437\u0435 \u0438 \u043F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0435\u0442\u0441\u044F \u043F\u0435\u0440\u0435\u0434 \u043E\u043F\u043B\u0430\u0442\u043E\u0439."
      });
      $[26] = t;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    let t12;
    if ($[28] !== t11) {
      t12 = <p className="max-w-2xl type-body1 text-osmix-text-2">{t11}</p>;
      $[28] = t11;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== t10 || $[31] !== t12) {
      t13 = <div className="flex flex-col gap-2">{t10}{t12}</div>;
      $[30] = t10;
      $[31] = t12;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    if ($[33] !== t13 || $[34] !== t8) {
      t2 = <div className="flex flex-col gap-6">{t8}{t13}</div>;
      $[33] = t13;
      $[34] = t8;
      $[35] = t2;
    } else {
      t2 = $[35];
    }
    t3 = config.isPending ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{["a", "b", "c"].map(_temp2)}</div> : config.error ? <ErrorState onRetry={() => config.refetch()} /> : <div className="flex flex-col gap-4"><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{methods.map(m => <section key={m.key} className="flex flex-col gap-3 rounded-[16px] bg-[#F8F8F8] p-5"><span className="flex size-11 items-center justify-center rounded-[12px] bg-white text-osmix-tangelo"><m.icon className="size-6" strokeWidth={1.5} /></span><h2 className="type-h5 text-osmix-text-1">{m.title}</h2><p className="flex-1 type-body2 text-osmix-text-2">{m.desc}</p><span className="type-body1-bold text-osmix-text-1">{m.fee != null && m.fee > 0 ? fmt(m.fee) : t("delivery.free", {
              defaultValue: "\u0411\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u043E"
            })}</span></section>)}</div>{freeOver != null && freeOver > 0 && <div className="flex items-center gap-3 rounded-[16px] bg-osmix-tangelo/5 p-5"><span className="flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-white text-osmix-tangelo"><Banknote className="size-6" strokeWidth={1.5} /></span><p className="type-body1 text-osmix-text-1">{t("delivery.freeOver", {
            defaultValue: "\u0411\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u0430\u044F \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0430 \u043F\u0440\u0438 \u0437\u0430\u043A\u0430\u0437\u0435 \u043E\u0442 {{amount}} \u0432 \u043E\u0434\u043D\u043E\u043C \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0435.",
            amount: fmt(freeOver)
          })}</p></div>}</div>;
    $[1] = config;
    $[2] = fees?.express;
    $[3] = fees?.pickup;
    $[4] = fees?.standard;
    $[5] = fmt;
    $[6] = freeOver;
    $[7] = t;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  let t4;
  if ($[36] !== t) {
    t4 = t("delivery.featuresTitle", {
      defaultValue: "\u041F\u0440\u0438\u043C\u0435\u0440\u043A\u0430 \u0438 \u0432\u044B\u043A\u0443\u043F"
    });
    $[36] = t;
    $[37] = t4;
  } else {
    t4 = $[37];
  }
  let t5;
  if ($[38] !== t4) {
    t5 = <h2 className="type-h2">{t4}</h2>;
    $[38] = t4;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Ruler className="size-6 shrink-0 text-osmix-tangelo" strokeWidth={1.5} />;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== t) {
    t7 = t("delivery.tryOn.title", {
      defaultValue: "\u041F\u0440\u0438\u043C\u0435\u0440\u043A\u0430"
    });
    $[41] = t;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== t7) {
    t8 = <h3 className="type-body1-bold text-osmix-text-1">{t7}</h3>;
    $[43] = t7;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  let t9;
  if ($[45] !== t) {
    t9 = t("delivery.tryOn.desc", {
      defaultValue: "\u0414\u043B\u044F \u0442\u043E\u0432\u0430\u0440\u043E\u0432 \u0441 \u043F\u0440\u0438\u043C\u0435\u0440\u043A\u043E\u0439 \u043C\u043E\u0436\u043D\u043E \u043E\u0446\u0435\u043D\u0438\u0442\u044C \u0432\u0435\u0449\u044C \u043F\u0440\u0438 \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u0438 \u0438 \u0432\u044B\u043A\u0443\u043F\u0438\u0442\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u0442\u043E, \u0447\u0442\u043E \u043F\u043E\u0434\u043E\u0448\u043B\u043E. \u0414\u043E\u0441\u0442\u0443\u043F\u043D\u043E\u0441\u0442\u044C \u043F\u0440\u0438\u043C\u0435\u0440\u043A\u0438 \u0432\u0438\u0434\u043D\u0430 \u043F\u0440\u0438 \u043E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u0438\u0438."
    });
    $[45] = t;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== t9) {
    t10 = <p className="type-body2 text-osmix-text-2">{t9}</p>;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== t10 || $[50] !== t8) {
    t11 = <article className="flex gap-3 rounded-[16px] border border-osmix-gray-300 p-5">{t6}<div className="flex flex-col gap-1">{t8}{t10}</div></article>;
    $[49] = t10;
    $[50] = t8;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Package className="size-6 shrink-0 text-osmix-tangelo" strokeWidth={1.5} />;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t) {
    t13 = t("delivery.partial.title", {
      defaultValue: "\u0427\u0430\u0441\u0442\u0438\u0447\u043D\u044B\u0439 \u0432\u044B\u043A\u0443\u043F"
    });
    $[53] = t;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] !== t13) {
    t14 = <h3 className="type-body1-bold text-osmix-text-1">{t13}</h3>;
    $[55] = t13;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t) {
    t15 = t("delivery.partial.desc", {
      defaultValue: "\u0418\u0437 \u0437\u0430\u043A\u0430\u0437\u0430 \u043C\u043E\u0436\u043D\u043E \u0432\u044B\u043A\u0443\u043F\u0438\u0442\u044C \u0447\u0430\u0441\u0442\u044C \u0442\u043E\u0432\u0430\u0440\u043E\u0432 \u2014 \u043E\u043F\u043B\u0430\u0442\u0430 \u0441\u043F\u0438\u0448\u0435\u0442\u0441\u044F \u0442\u043E\u043B\u044C\u043A\u043E \u0437\u0430 \u0432\u044B\u043A\u0443\u043F\u043B\u0435\u043D\u043D\u044B\u0435 \u043F\u043E\u0437\u0438\u0446\u0438\u0438. \u0417\u0430 \u043D\u0435\u0432\u044B\u043A\u0443\u043F\u043B\u0435\u043D\u043D\u044B\u0435 \u0438 \u0431\u0440\u0430\u043A \u0432\u043E\u0437\u0432\u0440\u0430\u0449\u0430\u0435\u0442\u0441\u044F \u0432\u0441\u044F \u0441\u0443\u043C\u043C\u0430, \u0432\u043A\u043B\u044E\u0447\u0430\u044F \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0443."
    });
    $[57] = t;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t15) {
    t16 = <p className="type-body2 text-osmix-text-2">{t15}</p>;
    $[59] = t15;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t14 || $[62] !== t16) {
    t17 = <article className="flex gap-3 rounded-[16px] border border-osmix-gray-300 p-5">{t12}<div className="flex flex-col gap-1">{t14}{t16}</div></article>;
    $[61] = t14;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t11 || $[65] !== t17) {
    t18 = <div className="grid gap-4 sm:grid-cols-2">{t11}{t17}</div>;
    $[64] = t11;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== t18 || $[68] !== t5) {
    t19 = <section className="flex flex-col gap-4">{t5}{t18}</section>;
    $[67] = t18;
    $[68] = t5;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  const t20 = encodeURI(DELIVERY_RULES_PDF);
  let t21;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <FileText className="size-5" />;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== t) {
    t22 = t("delivery.rulesPdf", {
      defaultValue: "\u041F\u043E\u043B\u043D\u044B\u0435 \u043F\u0440\u0430\u0432\u0438\u043B\u0430 \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0438 \u0438 \u043F\u0440\u0438\u043C\u0435\u0440\u043A\u0438 (PDF)"
    });
    $[71] = t;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== t22) {
    t23 = <a href={t20} target="_blank" rel="noreferrer" className="flex w-fit items-center gap-2 rounded-[12px] bg-osmix-text-1 px-5 py-3 type-button text-white outline-none transition-colors hover:bg-osmix-text-1/90 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{t21}{t22}</a>;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== t1 || $[76] !== t19 || $[77] !== t2 || $[78] !== t23 || $[79] !== t3) {
    t24 = <div className={t1}>{t2}{t3}{t19}{t23}</div>;
    $[75] = t1;
    $[76] = t19;
    $[77] = t2;
    $[78] = t23;
    $[79] = t3;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  return t24;
}
function _temp2(k) {
  return <Skeleton key={k} className="h-40 rounded-[16px]" />;
}
function _temp() {
  return api.settings.delivery();
}